import { useSyncExternalStore } from 'react';

// ── LA INTENCION DE RECUPERACION SE FOTOGRAFIA ANTES DE QUE LA BORREN ─────────
//
// ┌─ POR QUE ESTE ARCHIVO EXISTE ────────────────────────────────────────────────┐
// │ El enlace del correo aterriza en /login con                                  │
// │ `#access_token=...&type=recovery` en el hash. Ese `type=recovery` era la      │
// │ UNICA senal que la app miraba para saber que no estaba ante un login normal.  │
// │                                                                              │
// │ Pero supabase-js lo BORRA. `_getSessionFromURL()` termina con                 │
// │ `window.location.hash = ''` (auth-js 2.104, GoTrueClient.js:3087) y eso pasa  │
// │ DENTRO de `_initialize()`, que es exactamente lo que `getSession()` espera.   │
// │ Como `AuthProvider` no suelta `loading` hasta que `getSession()` vuelve,      │
// │ cuando la app por fin mira el hash el hash YA NO EXISTE — y con una sesion    │
// │ valida en la mano, `LoginRoute` mandaba al usuario al dashboard como si       │
// │ hubiera iniciado sesion normalmente. La pantalla de contrasena nueva era      │
// │ INALCANZABLE. No es una carrera que a veces se gane: el orden es siempre ese. │
// │                                                                              │
// │ Por eso la foto se toma en tiempo de IMPORTACION, y este modulo se importa    │
// │ desde `lib/supabase.js` ARRIBA DE TODO, antes de `createClient(...)`. Mover   │
// │ ese import debajo de la creacion del cliente vuelve a romper el flujo.        │
// └──────────────────────────────────────────────────────────────────────────────┘
//
// La segunda fuente —el evento `PASSWORD_RECOVERY` de `onAuthStateChange`— no
// reemplaza a la foto: la respalda. Es la unica senal que sobrevive si algun dia
// el proyecto pasa a flujo PKCE, donde la URL trae `?code=` y ningun `type`.

const CLAVE = 'feria_password_setup_intent';

// La intencion caduca sola. Sin esto, una foto que nadie limpio convierte /login
// en una pantalla de contrasena para el resto de la vida de la pestana.
const VIGENCIA_MS = 15 * 60 * 1000;

// El error del enlace (caducado, ya usado) vive SOLO esta carga de pagina: no se
// persiste a proposito, para que no reaparezca en la siguiente visita a /login.
let errorDeEnlace = null;

const oyentes = new Set();
let foto = { kind: null, linkError: null };

function leerParam(nombre) {
  if (typeof window === 'undefined') return null;
  const query = new URLSearchParams(window.location.search || '');
  const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  return hash.get(nombre) || query.get(nombre) || null;
}

function guardar(kind) {
  if (typeof window === 'undefined') return;
  try {
    // sessionStorage y no localStorage: la intencion muere con la pestana. Una
    // recuperacion abandonada no puede seguir viva manana en otra ventana.
    window.sessionStorage.setItem(CLAVE, JSON.stringify({ kind, at: Date.now() }));
  } catch {
    // Si el navegador no deja escribir, el flujo sigue con la senal en memoria.
  }
}

function leerGuardado() {
  if (typeof window === 'undefined') return null;
  try {
    const crudo = window.sessionStorage.getItem(CLAVE);
    if (!crudo) return null;
    const dato = JSON.parse(crudo);
    if (dato?.kind !== 'recovery' && dato?.kind !== 'invite') return null;
    if (Date.now() - Number(dato.at || 0) > VIGENCIA_MS) {
      window.sessionStorage.removeItem(CLAVE);
      return null;
    }
    return dato;
  } catch {
    return null;
  }
}

// `useSyncExternalStore` exige que la foto sea estable por referencia mientras no
// cambie nada; si se devolviera un objeto nuevo en cada lectura, React entraria en
// un bucle de renders.
function recomputar() {
  const kind = leerGuardado()?.kind || null;
  if (kind === foto.kind && errorDeEnlace === foto.linkError) return;
  foto = { kind, linkError: errorDeEnlace };
  oyentes.forEach(fn => { try { fn(); } catch { /* un oyente roto no tumba al resto */ } });
}

// ── LA FOTO ───────────────────────────────────────────────────────────────────
// Se toma al evaluar el modulo. Todo lo de abajo depende de que esto corra antes
// de que se cree el cliente de Supabase.
(function fotografiarLaUrl() {
  if (typeof window === 'undefined') return;

  const error = leerParam('error');
  const codigo = leerParam('error_code');
  const descripcion = leerParam('error_description');
  if (error || codigo || descripcion) {
    errorDeEnlace = { codigo: codigo || error || 'unspecified_error', descripcion: descripcion || '' };
  }

  const tipo = leerParam('type');
  const query = new URLSearchParams(window.location.search || '');
  if (tipo === 'recovery') {
    guardar('recovery');
  } else if (tipo === 'invite' || query.get('team_invite') === '1' || query.get('set_password') === '1') {
    guardar('invite');
  }
  // Ojo: una URL de error NO trae `type`. Por eso un enlace ya consumido no
  // vuelve a abrir el modo recuperacion — solo deja el mensaje. (Requisito G.)

  foto = { kind: leerGuardado()?.kind || null, linkError: errorDeEnlace };
})();

export function getPasswordSetupSnapshot() {
  return foto;
}

export function subscribePasswordSetup(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

// La llama `AuthContext` cuando Supabase emite `PASSWORD_RECOVERY`.
export function markPasswordRecovery() {
  guardar('recovery');
  recomputar();
}

// Se limpia en tres momentos, y los tres importan:
//   1. contrasena cambiada con exito     → si no, el guard deja al usuario en /login;
//   2. `SIGNED_IN` (login normal)        → asi una intencion vieja no secuestra la pantalla;
//   3. `SIGNED_OUT` / boton de salir     → la via de escape de la pantalla obligatoria.
export function clearPasswordSetupIntent() {
  if (typeof window !== 'undefined') {
    try { window.sessionStorage.removeItem(CLAVE); } catch { /* nada que limpiar */ }
  }
  errorDeEnlace = null;
  recomputar();
}

export function usePasswordSetupIntent() {
  return useSyncExternalStore(subscribePasswordSetup, getPasswordSetupSnapshot, getPasswordSetupSnapshot);
}
